// Function to generate UUID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Function to initiate URL extraction with frontend-generated UUID (fire and forget)
export const initiateUrlExtraction = async (url) => {
  try {
    // Generate UUID on the frontend
    const uuid = generateUUID();
    console.log('Generated UUID:', uuid, 'for URL:', url);
    
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "url": url,
      "uid": uuid
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    // Fire and forget - don't wait for Lambda response, just 5 second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      fetch("https://itgfsez4yzugg5sfkmts2u3vom0lmqbp.lambda-url.us-east-1.on.aws", {
        ...requestOptions,
        signal: controller.signal
      }).catch(() => {
        // Ignore any errors from Lambda - we'll poll Redis directly
        console.log('Lambda request sent, will poll Redis directly');
      });
    } catch (error) {
      // Ignore timeout or other errors
      console.log('Lambda request timeout or error, will poll Redis directly');
    } finally {
      clearTimeout(timeoutId);
    }

    return {
      success: true,
      uuid: uuid,
      message: 'Extraction initiated, polling Redis for results'
    };
  } catch (error) {
    console.error('Error initiating URL extraction:', error);
    throw new Error(error.message || 'Failed to initiate URL extraction');
  }
};

// Function to extract learning goals by polling Redis directly using UID
export const extractLearningGoalsFromUrl = async (uuid) => {
  try {
    console.log('Polling Redis directly for UID:', uuid);
    
    const pollRedis = async () => {
      // Fetch directly from Redis using UID as key
      const response = await fetch('https://communal-locust-56899.upstash.io/get/' + uuid, {
        headers: {
          'Authorization': 'Bearer At5DAAIgcDGz2M3fkHsZg3a7e0_k_VwMTpg6Ad0zlbuw9Kr3UsPZYA'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data from Redis: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Redis direct response:', data);

      return data;
    };

    // Poll every 5 seconds until we get data
    const maxPollingTime = 300000; // 5 minutes max
    const pollInterval = 5000; // 5 seconds
    const startTime = Date.now();

    while (Date.now() - startTime < maxPollingTime) {
      try {
        const data = await pollRedis();
        
        // Check if we have data (Redis returns {result: "JSON_STRING"} or {result: null})
        if (data && data.result) {
          let parsedResult;
          
          // If result is a string, parse it as JSON
          if (typeof data.result === 'string') {
            try {
              parsedResult = JSON.parse(data.result);
            } catch (parseError) {
              console.log('Failed to parse Redis result as JSON:', parseError);
              throw new Error('Invalid JSON in Redis result');
            }
          } else {
            // If result is already an object
            parsedResult = data.result;
          }
          
          // Check if we have valid frameworks data
          if (parsedResult && parsedResult.success && parsedResult.frameworks && Array.isArray(parsedResult.frameworks) && parsedResult.frameworks.length > 0) {
            console.log('Successfully received frameworks data from Redis:', parsedResult);
            
            return {
              success: true,
              frameworks: parsedResult.frameworks,
              extractedFrom: parsedResult.extracted_from || '',
              method: parsedResult.method || 'openai',
              source: parsedResult.source || 'firecrawl'
            };
          }
        }
        
        // If no data yet, wait 5 seconds before next poll
        console.log('No data yet in Redis, waiting 5 seconds before next poll...');
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
      } catch (pollError) {
        console.log('Redis polling error, retrying in 5 seconds:', pollError.message);
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }
    
    // If we reach here, polling timed out
    throw new Error('Timeout: Data not available in Redis after 5 minutes');
    
  } catch (error) {
    console.error('Error extracting learning goals from Redis:', error);
    throw new Error(error.message || 'Failed to extract learning goals from Redis');
  }
};

// Legacy function names for backward compatibility
export const scrapeWebsiteWithFirecrawl = async (url) => {
  // This function is now deprecated, redirect to the new function
  throw new Error('This function is deprecated. Use extractLearningGoalsFromUrl instead.');
};

export const extractLearningGoalsWithOpenAI = async (content, url) => {
  // This function is now deprecated, redirect to the new function
  throw new Error('This function is deprecated. Use extractLearningGoalsFromUrl instead.');
};

// Updated combined function to use two-step process: initiate extraction -> poll Redis
export const scrapeAndExtractLearningGoals = async (url, onProgress = null) => {
  try {
    // Initiate extraction and get UUID
    if (onProgress) onProgress(1);
    console.log('Initiating extraction for URL:', url);
    const initiationResult = await initiateUrlExtraction(url);
    
    if (!initiationResult.success || !initiationResult.uuid) {
      throw new Error('Failed to initiate extraction process');
    }
    
    console.log('Got UUID:', initiationResult.uuid);
    
    // Poll Redis using the UUID
    console.log('Polling Redis for results...');
    const result = await extractLearningGoalsFromUrl(initiationResult.uuid);
    
    return {
      success: true,
      frameworks: result.frameworks,
      extractedFrom: result.extractedFrom,
      method: result.method,
      source: result.source,
      uuid: initiationResult.uuid // Include the UUID for reference
    };
  } catch (error) {
    console.error('Error in learning goals extraction process:', error);
    throw error;
  }
}; 
